import React, { useMemo } from "react";
import { Trans, useTranslation } from "@coworker/locales";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { useTestbuys } from "../CommonComponents/Repositories/useTestbuyService";
import { useMyStore } from "@coworker/app/src/core/auth/useLoggedInUser";
import { TestBuyList } from "./TestBuyList";
import { HfbRanks } from "./Analytics/Ranking/HfbRanks";
import EmptyPage from "../../EmptyStateScreen";
import { COMPLETED_STATUS } from "../constants";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { ActionBar } from "@coworker/apprestructured/src/layout/components/ActionBar/ActionBar";
import "../CommonComponents/styles.css";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useStartEndDates } from "../useStartEndDates";
import { useTestBuyFilters } from "../hooks/useTestBuyFilters";
import { FilterPanel } from "@coworker/apprestructured/src/shared/filters/components/FilterPanel";
import { getSelectedFilterValue } from "@coworker/apprestructured/src/shared/filters/utils/filter/filtervalue";
import {
  DEFUALT_PERIOD_VALUE,
  PERIOD_ID,
} from "../../MFAQ/hooks/usePeriodFilter";
import {
  DEFAULT_LOCATION_VALUE,
  LOCATION_ID,
} from "../hooks/useLocationFilter";
import { PeriodFilterValue } from "@coworker/apprestructured/src/shared/filters/types/filter";
import { FilterKeys } from "../../InputPopup/UserFiltersRow/filterChoices";

export const TestBuyOverview = () => {
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const store = useMyStore();
  const tz =
    store?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const navigate = useFixaNavigate();

  const testbuyFilters = useTestBuyFilters();

  const period = getSelectedFilterValue(
    PERIOD_ID,
    testbuyFilters.filterValues,
    DEFUALT_PERIOD_VALUE
  ) as PeriodFilterValue;

  const location = getSelectedFilterValue(
    LOCATION_ID,
    testbuyFilters.filterValues,
    DEFAULT_LOCATION_VALUE
  ).value as string;

  const filters = useMemo(() => {
    return [period.value, location];
  }, [period, location]) as FilterKeys;

  const { startDate, endDate } = useStartEndDates(
    period.value,
    period.startDate,
    period.endDate,
    tz
  );

  const { testbuys, loading } = useTestbuys(location, startDate, endDate);
  const { testbuys: allTestbuys } = useTestbuys(undefined, startDate, endDate);

  const [isWholeStoreActive, setIsWholeStoreActive] =
    React.useState<boolean>(false);
  const [totalTestbuysDone, setTotalTestbuysDone] = React.useState<number>();
  React.useEffect(() => {
    if (!location || location === "whole=whole") {
      setIsWholeStoreActive(true);
    } else {
      setIsWholeStoreActive(false);
    }
  }, [location, isWholeStoreActive]);

  React.useEffect(() => {
    if (testbuys && filters) {
      const filterDateThreshold = 1661990400000; // 1st of September 2022
      const completedTestbuys = testbuys.filter(
        (tb) =>
          (tb.finishedAtTs ?? 0) >= filterDateThreshold &&
          tb.status === COMPLETED_STATUS
      );
      setTotalTestbuysDone(completedTestbuys.length);
    }
  }, [testbuys, filters]);

  const { t } = useTranslation();

  return (
    //This div exist due to render issues on RDT
    <div>
      <div>
        <Linkbar currentLabel={t("testBuysString")} currentAsH1 />
        <FilterPanel
          filterValues={testbuyFilters.filterValues}
          setFilterValues={testbuyFilters.setFilterValues}
          useFiltersFactory={testbuyFilters.useFiltersFactory}
          feature="test-buy"
        />
        {testbuys?.length === 0 && !loading && (
          <EmptyPage
            titleKey="noTestbuysYetString"
            subtitleKey="tapOnTheButtonToStartTestbuyString"
          />
        )}
        <HfbRanks
          testbuys={testbuys ?? []}
          allTestbuys={allTestbuys ?? []}
          filters={filters}
          isWholeStoreActive={isWholeStoreActive}
          totalTestbuysDone={totalTestbuysDone ?? 0}
          loading={loading}
        />
        <TestBuyList testbuys={testbuys ?? []} loading={loading} />
        <ActionBar hidden={false}>
          {
            <FixaButton
              data-testid="button-startNewTestBuy"
              fluid={true}
              type="primary"
              text={<Trans>createTestbuyString</Trans>}
              onClick={() => {
                navigate("/testbuy/new");
              }}
              aria-label={`${(<Trans>createTestbuyString</Trans>)}`}
            />
          }
        </ActionBar>
      </div>
    </div>
  );
};
